(() => {
  const ROOT_SELECTOR = '[data-fk-projects-gallery]';
  const FILTER_KEYS = {
    venue: ['filterVenue', 'filterVenueLabel'],
    budget: ['filterBudget', 'filterBudgetLabel'],
    location: ['filterLocation', 'filterLocationLabel'],
    type: ['filterType', 'filterTypeLabel']
  };

  function initialiseGallery(root) {
    if (!root || root.dataset.projectsGalleryInitialised === 'true') return;

    const cards = Array.from(root.querySelectorAll('[data-project-card]'));
    const filters = Array.from(root.querySelectorAll('[data-project-filter]'));
    const viewport = root.querySelector('[data-project-viewport]');
    const track = root.querySelector('[data-project-track]');
    const previousButton = root.querySelector('[data-project-previous]');
    const nextButton = root.querySelector('[data-project-next]');
    const dots = root.querySelector('[data-project-dots]');
    const status = root.querySelector('[data-project-status]');
    const emptyMessage = root.querySelector('[data-project-empty]');
    const filtersContainer = root.querySelector('[data-project-filters]');
    const itemSingular = root.dataset.itemSingular || 'project';
    const itemPlural = root.dataset.itemPlural || 'projects';

    if (!cards.length || !viewport || !track || !previousButton || !nextButton || !dots) return;

    root.dataset.projectsGalleryInitialised = 'true';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = null;
    let itemsPerPage = 1;
    let pageCount = 1;

    function visibleCards() {
      return cards.filter((card) => !card.hidden);
    }

    function populateFilters() {
      filters.forEach((select) => {
        const key = select.dataset.projectFilter;
        const dataKeys = FILTER_KEYS[key];
        const wrapper = root.querySelector(`[data-filter-wrapper="${key}"]`);
        if (!dataKeys) return;

        const options = new Map();
        cards.forEach((card) => {
          const value = card.dataset[dataKeys[0]];
          const label = card.dataset[dataKeys[1]];
          if (value && label && !options.has(value)) options.set(value, label);
        });

        if (options.size < 2) {
          select.disabled = true;
          if (wrapper) wrapper.hidden = true;
          return;
        }

        Array.from(options.entries())
          .sort((first, second) => first[1].localeCompare(second[1]))
          .forEach(([value, label]) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = label;
            select.appendChild(option);
          });
      });
    }

    function cardDataValue(card, filterKey) {
      const dataKeys = FILTER_KEYS[filterKey];
      return dataKeys ? card.dataset[dataKeys[0]] : '';
    }

    function updateControls() {
      const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const tolerance = 2;
      previousButton.disabled = viewport.scrollLeft <= tolerance;
      nextButton.disabled = viewport.scrollLeft >= maximumScroll - tolerance;

      const card = visibleCards()[0];
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : viewport.clientWidth;
      const pageWidth = Math.max(1, step * itemsPerPage);
      const activePage = Math.min(pageCount - 1, Math.max(0, Math.round(viewport.scrollLeft / pageWidth)));

      dots.querySelectorAll('[data-project-page]').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === activePage ? 'true' : 'false');
      });
    }

    function scrollToPage(index) {
      const card = visibleCards()[0];
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const step = card ? card.getBoundingClientRect().width + gap : viewport.clientWidth;
      viewport.scrollTo({
        left: index * step * itemsPerPage,
        behavior: reducedMotion ? 'auto' : 'smooth'
      });
    }

    function rebuildPagination() {
      const visible = visibleCards();
      const firstCard = visible[0];

      if (!visible.length || !firstCard || viewport.clientWidth === 0) return;

      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0;
      const cardWidth = firstCard.getBoundingClientRect().width;
      itemsPerPage = Math.max(1, Math.floor((viewport.clientWidth + gap) / (cardWidth + gap)));
      pageCount = Math.max(1, Math.ceil(visible.length / itemsPerPage));
      dots.replaceChildren();

      for (let index = 0; index < pageCount; index += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'fk-projects-gallery__dot';
        dot.dataset.projectPage = String(index);
        dot.setAttribute('aria-label', `Go to ${itemSingular} page ${index + 1}`);
        dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        dot.addEventListener('click', () => scrollToPage(index));
        dots.appendChild(dot);
      }

      dots.hidden = pageCount <= 1;
      updateControls();
    }

    function queueLayoutUpdate() {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        rebuildPagination();
        frame = null;
      });
    }

    function applyFilters() {
      cards.forEach((card) => {
        card.hidden = filters.some((select) => {
          if (select.disabled || !select.value || select.value === 'all') return false;
          return cardDataValue(card, select.dataset.projectFilter) !== select.value;
        });
      });

      if (filtersContainer) {
        filtersContainer.hidden = !filters.some((select) => !select.disabled);
      }

      const count = visibleCards().length;
      if (status) status.textContent = `${count} ${count === 1 ? itemSingular : itemPlural} shown`;
      if (emptyMessage) emptyMessage.hidden = count !== 0;
      viewport.hidden = count === 0;
      previousButton.hidden = count === 0;
      nextButton.hidden = count === 0;
      dots.hidden = count === 0;
      viewport.scrollLeft = 0;
      queueLayoutUpdate();
    }

    function move(direction) {
      const activeDot = dots.querySelector('[data-project-page][aria-current="true"]');
      const currentPage = activeDot ? Number(activeDot.dataset.projectPage) : 0;
      const targetPage = Math.min(pageCount - 1, Math.max(0, currentPage + direction));
      scrollToPage(targetPage);
    }

    filters.forEach((select) => select.addEventListener('change', applyFilters));
    previousButton.addEventListener('click', () => move(-1));
    nextButton.addEventListener('click', () => move(1));
    viewport.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        move(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        move(1);
      }
    });
    viewport.addEventListener('scroll', () => window.requestAnimationFrame(updateControls), { passive: true });
    window.addEventListener('resize', queueLayoutUpdate, { passive: true });

    populateFilters();
    applyFilters();
  }

  function initialiseAll(scope) {
    if (scope.matches && scope.matches(ROOT_SELECTOR)) initialiseGallery(scope);
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialiseGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll(document));
  } else {
    initialiseAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();
